import { Box, Group, Skeleton, Stack, Tabs } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	apiGetIntegrationUserCredentialsQueryKey,
	useApiGetIntegrationUserCredentials,
	useApiSetIntegrationUserCredentials,
	useApiTestUserCredentialsConnection,
} from '@repo/api-codegen';
import { integrationSchemas } from '@repo/common/constants/integration/integrations.schemas';
import { Banner, Button, TextArea, TextInput } from '@repo/foundations';
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { keys } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { api } from '../../../../../../network';
import type { IIntegration } from '../../../../../api';
import { IntegrationOauthConnectController } from '../../../../../features/IntegrationOauthConnect';
import type { BuiltinIntegrationSpec } from '../../../../../interfaces/IntegrationSpec';
import { snakeCaseToTitleCase } from '../../../../../utils/shared.utils';

interface UserLevelCredentialsManagerProps {
	integration: IIntegration;
	spec: BuiltinIntegrationSpec;
	onCredentialsSaved?: () => void;
}

interface YupField {
	spec?: {
		meta?: {
			groups?: string[];
			group?: string;
			htmlType?: string;
			label?: string;
			description?: string;
			placeholder?: string;
		};
		tests?: Array<{ OPTIONS?: { name?: string } }>;
		default?: string;
	};
	meta?: {
		groups?: string[];
		group?: string;
		htmlType?: string;
		label?: string;
		description?: string;
		placeholder?: string;
	};
	tests?: Array<{ OPTIONS?: { name?: string } }>;
	default?: string;
}

interface YupSchema {
	fields?: Record<string, YupField>;
}

interface FieldGroups {
	[groupName: string]: Record<string, YupField>;
}

export function UserLevelCredentialsManager({
	integration,
	spec,
	onCredentialsSaved,
}: UserLevelCredentialsManagerProps) {
	const queryClient = useQueryClient();

	const { data: userCredentials, isLoading: isLoadingCredentials } =
		useApiGetIntegrationUserCredentials({
			pathParams: { integrationId: integration.id },
		});

	const { mutateAsync: setUserCredentials } =
		useApiSetIntegrationUserCredentials({});
	const { mutateAsync: testUserCredentials } =
		useApiTestUserCredentialsConnection({});

	const [isSaving, setIsSaving] = useState(false);
	const [isTesting, setIsTesting] = useState(false);
	const [testError, setTestError] = useState<string | null>(null);
	const [testSuccess, setTestSuccess] = useState(false);

	// Get integration schema and user level credentials fields
	const integrationSchema = useMemo(
		() => integrationSchemas[integration.type] as YupSchema | undefined,
		[integration.type]
	);
	const userLevelCredentialsFields = spec.userLevelCredentialsFields || [];

	// Check if this is BigQuery (has special OAuth handling)
	const isBigQuery = integration.type === 'bigquery';

	// Check if integration has OAuth enabled (oauth_enabled in credentials)
	const isOauthEnabled = Boolean((integration.credentials ?? {}).oauth_enabled);
	const showOauthConnect = isOauthEnabled;

	const groups = useMemo(() => {
		if (!integrationSchema) return { default: {} };

		const groups: FieldGroups = {};

		// Extract fields from Yup schema
		const schemaFields = integrationSchema.fields || {};

		keys(schemaFields).forEach((fieldName) => {
			// Only include fields that are in userLevelCredentialsFields
			if (!userLevelCredentialsFields.includes(fieldName)) {
				return;
			}

			const field = schemaFields[fieldName];
			const fieldMeta = field?.spec?.meta;
			const fieldGroups =
				fieldMeta?.groups ||
				(fieldMeta?.group ? [fieldMeta.group] : ['default']);

			fieldGroups.forEach((groupName: string) => {
				if (!groups[groupName]) {
					groups[groupName] = {};
				}
				groups[groupName][fieldName] = field;
			});
		});

		// Remove the _group field from groups
		Object.keys(groups).forEach((groupName) => {
			delete groups[groupName]._group;
		});

		// Special handling for BigQuery - always show OAuth option
		if (isBigQuery) {
			// Ensure we have both Credentials and OAuth groups
			if (!groups['Credentials']) {
				groups['Credentials'] = {};
			}
			if (!groups['OAuth']) {
				groups['OAuth'] = {};
			}

			// Move credentials field to Credentials group
			if (groups['default'] && groups['default']['credentials']) {
				groups['Credentials']['credentials'] = groups['default']['credentials'];
				delete groups['default']['credentials'];
			}

			// Remove default group if empty
			if (groups['default'] && Object.keys(groups['default']).length === 0) {
				delete groups['default'];
			}
		}

		// Filter out groups that have no fields (except OAuth for BigQuery)
		const filteredGroups: FieldGroups = {};
		Object.keys(groups).forEach((groupName) => {
			if (
				Object.keys(groups[groupName]).length > 0 ||
				(isBigQuery && groupName === 'OAuth')
			) {
				filteredGroups[groupName] = groups[groupName];
			}
		});

		return Object.keys(filteredGroups).length > 0
			? filteredGroups
			: { default: {} };
	}, [integrationSchema, userLevelCredentialsFields, isBigQuery]);

	const hasMultipleGroups = keys(groups).length > 1;

	// Determine initial group selection
	const [selectedGroup, setSelectedGroup] = useState<string>(() => {
		if (!hasMultipleGroups) return keys(groups)[0] || 'default';

		// Check if user has existing credentials with a _group field
		const existingGroup = userCredentials?.credentials?._group;
		if (existingGroup && groups[existingGroup]) {
			return existingGroup;
		}

		// For BigQuery, prefer 'Credentials' as default
		if (isBigQuery && groups['Credentials']) {
			return 'Credentials';
		}

		// Get the default from the schema's _group field
		const groupField = integrationSchema?.fields?._group;
		const schemaDefault = groupField?.spec?.default || groupField?.default;
		if (schemaDefault && groups[schemaDefault]) {
			return schemaDefault;
		}

		// Fallback to first group
		return keys(groups)[0] || 'default';
	});

	// Check if OAuth is an option and if OAuth is selected
	const hasOAuthGroup = isBigQuery && groups['OAuth'];
	const isOAuthSelected = selectedGroup === 'OAuth';

	// Get the validation schema for the selected group
	const selectedSchema = groups[selectedGroup] || {};
	const fieldsInCurrentGroup = userLevelCredentialsFields.filter((field) =>
		keys(selectedSchema).includes(field)
	);

	// Create validation schema
	const validationSchema = useMemo(() => {
		const schemaShape: Record<string, Yup.AnySchema> = {};

		fieldsInCurrentGroup.forEach((field) => {
			const fieldSchema = selectedSchema[field];
			const tests = fieldSchema?.spec?.tests || fieldSchema?.tests || [];
			const isRequired = tests.some(
				(test) => test.OPTIONS?.name === 'required'
			);

			let schema = Yup.string();

			if (isRequired) {
				schema = schema.required(`${snakeCaseToTitleCase(field)} is required`);
			}

			schemaShape[field] = schema;
		});

		return Yup.object().shape(schemaShape);
	}, [fieldsInCurrentGroup, selectedSchema]);

	// Create initial values
	const initialValues = useMemo(() => {
		const values: Record<string, string> = {};

		fieldsInCurrentGroup.forEach((field) => {
			const existingValue = userCredentials?.credentials?.[field];
			const fieldSchema = selectedSchema[field];
			const defaultValue =
				fieldSchema?.spec?.default || fieldSchema?.default || '';

			values[field] =
				existingValue && existingValue !== '**redacted**'
					? existingValue
					: defaultValue;
		});

		return values;
	}, [fieldsInCurrentGroup, userCredentials?.credentials, selectedSchema]);

	const handleStartOAuth = useCallback(() => {
		if (!isBigQuery) return;

		// Use the same pattern as existing OAuth flows
		const baseUrl = api();
		const authUrl = `${baseUrl}oauth/to_oauth/${integration.id}/?user_level=true`;
		window.open(authUrl, '_self');
	}, [isBigQuery, integration.id]);

	const formik = useFormik({
		initialValues,
		validationSchema,
		enableReinitialize: true,
		onSubmit: async (values) => {
			setIsSaving(true);

			if (!isTesting) {
				setTestError(null);
				setTestSuccess(false);
			}

			try {
				const credentialsToSave = { ...values };
				if (hasMultipleGroups) {
					credentialsToSave._group = selectedGroup;
				}

				await setUserCredentials({
					pathParams: { integrationId: integration.id },
					body: { credentials: credentialsToSave },
				});

				// Reset form dirty state
				Object.keys(formik.values).forEach((key) => {
					formik.setFieldTouched(key, false, false);
				});
				formik.setStatus('submitted');

				if (!isTesting || testSuccess) {
					showNotification({
						title: 'Success',
						message: 'User credentials saved successfully',
						color: 'green',
					});
				}

				if (!isTesting) {
					// Call the callback after successful save
					onCredentialsSaved?.();
				}

				// Invalidate the user credentials query so fresh data is loaded next time
				queryClient.invalidateQueries({
					queryKey: apiGetIntegrationUserCredentialsQueryKey({
						pathParams: { integrationId: integration.id },
					}),
				});
			} catch (error) {
				const errorMessage =
					error instanceof Error
						? error.message
						: 'Failed to save user credentials';
				showNotification({
					title: 'Error',
					message: errorMessage,
					color: 'red',
				});

				if (isTesting) {
					// Re-throw error so handleTestConnection can detect save failure
					throw error;
				}
			} finally {
				setIsSaving(false);
			}
		},
	});

	const handleTestConnection = useCallback(async () => {
		setIsTesting(true);
		setTestError(null);
		setTestSuccess(false);

		try {
			// Save credentials first if form is dirty
			if (formik.dirty && formik.isValid) {
				try {
					await formik.submitForm();
				} catch (submitError) {
					const errorMessage =
						submitError instanceof Error
							? submitError.message
							: 'Failed to save user credentials, please try again';
					setTestError(errorMessage);
					return;
				}
			}

			// Test the connection
			const result = await testUserCredentials({
				pathParams: { integrationId: integration.id },
			});

			if (result.success) {
				setTestSuccess(true);
				showNotification({
					title: 'Success',
					message: 'Credentials saved and connection test successful',
					color: 'green',
				});
				// Mark form as saved
				formik.resetForm({ values: formik.values });

				// Call the callback after successful test to close modal
				onCredentialsSaved?.();
			} else {
				setTestError(result.error || 'Connection test failed');
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : 'Connection test failed';
			setTestError(errorMessage);
		} finally {
			setIsTesting(false);
		}
	}, [formik, integration.id, testUserCredentials, onCredentialsSaved]);

	const handleSelectGroup = useCallback(
		(group: string | null) => {
			// Prevent tab changes while testing or saving
			if (isTesting || isSaving) {
				return;
			}
			if (group) {
				setSelectedGroup(group);
				setTestError(null);
				setTestSuccess(false);
			}
		},
		[isTesting, isSaving]
	);

	const renderField = useCallback(
		(field: string) => {
			const fieldSchema = selectedSchema[field];
			const fieldMeta = fieldSchema?.spec?.meta || fieldSchema?.meta || {};
			const htmlType = fieldMeta.htmlType || 'text';
			const label = fieldMeta.label || snakeCaseToTitleCase(field);
			const { description } = fieldMeta;
			const { placeholder } = fieldMeta;

			const commonProps = {
				key: field,
				name: field,
				label,
				description,
				placeholder,
				value: formik.values[field] || '',
				onChange: formik.handleChange,
				onBlur: formik.handleBlur,
				disabled: isTesting || isSaving,
				error:
					formik.touched[field] && formik.errors[field]
						? String(formik.errors[field])
						: undefined,
			};

			if (htmlType === 'textarea') {
				return <TextArea {...commonProps} rows={4} />;
			}

			if (htmlType === 'password') {
				return <TextInput {...commonProps} type="password" />;
			}

			return <TextInput {...commonProps} />;
		},
		[selectedSchema, formik, isTesting, isSaving]
	);

	if (isLoadingCredentials) {
		return <Skeleton height={200} />;
	}

	if (!userLevelCredentialsFields || userLevelCredentialsFields.length === 0) {
		return (
			<Banner
				tone="info"
				title="Not supported"
				message="This integration does not support personal credentials"
			/>
		);
	}

	if (showOauthConnect) {
		return (
			<IntegrationOauthConnectController
				integrationId={integration.id}
				integrationType={integration.type}
				isOauthEnabled={showOauthConnect}
			/>
		);
	}

	const disableFormButtons =
		!formik.isValid ||
		!formik.dirty ||
		formik.isSubmitting ||
		isSaving ||
		isTesting;

	return (
		<Stack spacing="md">
			<Box>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						formik.handleSubmit(e);
					}}
				>
					<Stack spacing="md">
						{hasMultipleGroups ? (
							<Tabs
								value={selectedGroup}
								onTabChange={handleSelectGroup}
								variant="pills"
							>
								<Tabs.List>
									{keys(groups).map((group) => (
										<Tabs.Tab key={group} value={group}>
											{group}
										</Tabs.Tab>
									))}
								</Tabs.List>

								{keys(groups).map((group) => {
									const groupSchema = groups[group] || {};
									const fieldsInGroup = userLevelCredentialsFields.filter(
										(field) =>
											keys(groupSchema).includes(field) || !hasMultipleGroups
									);

									return (
										<Tabs.Panel key={group} value={group}>
											<Stack spacing="md" mt="md">
												{group === 'OAuth' ? (
													<Button variant="primary" onClick={handleStartOAuth}>
														Connect
													</Button>
												) : (
													fieldsInGroup.map((field) => {
														const fieldSchema = groupSchema[field];
														const fieldMeta =
															fieldSchema?.spec?.meta ||
															fieldSchema?.meta ||
															{};
														const htmlType = fieldMeta.htmlType || 'text';
														const label =
															fieldMeta.label || snakeCaseToTitleCase(field);
														const { description } = fieldMeta;
														const { placeholder } = fieldMeta;

														const commonProps = {
															name: field,
															label,
															description,
															placeholder,
															value: formik.values[field] || '',
															onChange: formik.handleChange,
															onBlur: formik.handleBlur,
															disabled: isTesting || isSaving,
															error:
																formik.touched[field] && formik.errors[field]
																	? String(formik.errors[field])
																	: undefined,
														};

														if (htmlType === 'textarea') {
															return (
																<TextArea
																	key={field}
																	{...commonProps}
																	rows={4}
																/>
															);
														}

														if (htmlType === 'password') {
															return (
																<TextInput
																	key={field}
																	{...commonProps}
																	type="password"
																/>
															);
														}

														return <TextInput key={field} {...commonProps} />;
													})
												)}
											</Stack>
										</Tabs.Panel>
									);
								})}
							</Tabs>
						) : (
							<Stack spacing="md">
								{fieldsInCurrentGroup.map(renderField)}
							</Stack>
						)}

						{testError && (
							<Banner
								tone="critical"
								title="Connection failed"
								message={testError}
							/>
						)}

						{!isOAuthSelected && (
							<Group position="right" spacing="sm">
								<Button
									variant="tertiary"
									onClick={handleTestConnection}
									loading={isTesting}
									disabled={disableFormButtons}
								>
									Save and test connection
								</Button>
								<Button
									variant="primary"
									type="button"
									onClick={() => formik.handleSubmit()}
									loading={isSaving}
									disabled={disableFormButtons}
								>
									Save and close
								</Button>
							</Group>
						)}
					</Stack>
				</form>
			</Box>
		</Stack>
	);
}
